import React from "react";
import { Outlet, Link, useLocation, NavLink } from "react-router-dom";
import { UserMenu } from "./UserMenu";
import { PWAInstaller } from "./PWAInstaller";
import { OfflineIndicator } from "./OfflineIndicator";
import { Home, Upload, Settings, Video } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";

export function Layout() {
  const location = useLocation();
  const { user } = useAuth();

  const navigation = [
    { name: "Home", href: "/", icon: Home },
    { name: "Manage", href: "/manage", icon: Upload },
    ...(user?.admin ? [{ name: "Admin", href: "/admin", icon: Settings }] : []),
  ];

  return (
    <div className="min-h-screen w-full flex flex-col">
      {/* Desktop Header */}
      <header className="hidden md:block bg-background/95 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold"
          >
            <Video className="h-6 w-6" />
            RGPV
          </Link>
          <nav className="flex items-center gap-6">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors hover:text-primary",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>
          <UserMenu />
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden bg-background/95 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold">
            <Video className="h-6 w-6" />
            RGPV
          </Link>
          <UserMenu />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-6 pb-20 md:pb-6">
        <div className="container-fluid px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-40">
        <div className="grid grid-cols-3 gap-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex flex-col items-center justify-center py-2 text-xs font-medium transition-colors",
                  isActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-primary"
                )}
              >
                <Icon className="h-5 w-5 mb-1" />
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* PWA Components */}
      <PWAInstaller />
      <OfflineIndicator />
    </div>
  );
}
